// ============================================================================
// apps/web/src/lib/use-commandes-count.ts
// ----------------------------------------------------------------------------
// Compteur de « commandes cuisine reçues » en attente (statut en_attente) —
// alimente le badge de l'entrée « Production » (sidebar + tiroir + tuile accueil),
// comme les badges Newsletter / Portage / Candidatures / To-Do.
//
// Source : GET /api/production/order-requests?statut=en_attente (champ `pending`),
// via listOrderRequests. Réservé aux profils cuisine (cuisine.read).
//
// Rafraîchi : au montage, au focus / retour d'onglet, toutes les 60 s, et à la
// demande via notifyCommandesChanged() (appelé par CommandesTab à chaque décision).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from './auth-context';
import { Capability } from '@magic/shared';
import { listOrderRequests } from '@/pages/production/api';

const EVENT = 'magic:commandes-changed';

// À appeler après toute décision (accepter/refuser) pour rafraîchir le badge aussitôt.
export function notifyCommandesChanged(): void {
  window.dispatchEvent(new Event(EVENT));
}

export function useNewCommandesCount(): number {
  const { hasCapability } = useAuth();
  const canSee = hasCapability(Capability.CUISINE_READ);
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!canSee) { setCount(0); return; }
    try {
      const res = await listOrderRequests('en_attente');
      setCount(res.pending ?? 0);
    } catch { /* silencieux : le badge n'est pas critique */ }
  }, [canSee]);

  useEffect(() => {
    if (!canSee) { setCount(0); return; }
    void refresh();
    const onFocus = () => { void refresh(); };
    const onVis = () => { if (document.visibilityState === 'visible') void refresh(); };
    const onEvt = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener(EVENT, onEvt);
    const id = window.setInterval(() => { void refresh(); }, 60_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener(EVENT, onEvt);
      window.clearInterval(id);
    };
  }, [canSee, refresh]);

  return count;
}
